// ReferralPayoutModal.js - Updated version

import React, { useState, useEffect, useCallback } from "react";
import { useSuperAdmin } from "../context/SuperAdminContext";
import ReferralPayoutCard from "../components/ReferralPayoutCard";
import referral from "../assets/referral.png";

const USE_REAL_API = true; // switched to real API

const MAX_ELIGIBLE_REFERRALS = 10;

// ─────────────────────────────────────────────────────────────────────────────
// Transform the API response into the shape ReferralPayoutCard expects
//
// API shape:
// {
//   success: true,
//   data: {
//     totalPayout: 0,
//     totalReferralReceived: 0,
//     totalReferralYet: 0,
//     currentMonth: {
//       month: "May 2026",
//       myShifts: 0,
//       referralAmount: 0,
//       eligibleWorkerCount: 0,
//       activeWorkerCount: 0,
//       referredBreakup: []           // array of referred-worker objects
//     },
//     history: []                     // array of past-month objects (same shape as currentMonth)
//   }
// }
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a monthKey string like "2026-5" from a month label like "May 2026".
 */
const toMonthKey = (monthStr) => {
  if (!monthStr) return "-";
  return monthStr.replace(
    /(\w+)\s(\d{4})/,
    (_, m, y) => `${y}-${new Date(`${m} 1, ${y}`).getMonth() + 1}`
  );
};

/**
 * Transform a single month block (currentMonth or a history entry).
 *
 * referredBreakup items – we assume each item may have fields like:
 *   { phone, name, currentMonthShiftCount, referAmountEarned, referralBonusReceived, ... }
 * All fields are optional; missing values fall back to "-" / 0.
 */
const transformMonthBlock = (block, isCurrentMonth = false) => {
  if (!block) return null;

  const monthStr        = block.month          ?? "-";
  const totalShifts     = block.myShifts       ?? 0;
  const referralAmount  = block.referralAmount  ?? 0;
  const paymentStatus   = block.paymentStatus  ?? null; // "success" | "failed" | "payoutGenerated" | null

  // ✅ currentMonth uses "referredBreakup", history uses "breakdown"
  const referredBreakup = block.referredBreakup ?? block.breakdown ?? [];

  // ✅ currentMonth uses "activeWorkerCount", history uses "totalWorkerCount"
  const eligibleWorkerCount = block.eligibleWorkerCount ?? 0;
const totalReferrals = block.totalWorkerCount ?? referredBreakup.length ?? 0;

  const referrals = referredBreakup.map((w) => {
    // ✅ currentMonth uses "shifts", history uses "shiftCount"
    const shiftsCompleted =
      w?.shifts     ??   // currentMonth field
      w?.shiftCount ??   // history field
      0;

    const amount = w?.referAmountEarned ?? w?.amount ?? 0;

    return {
      name:  w?.name  || "-",
      phone: w?.phone ? `+91 ${String(w.phone).replace(/^91/, "")}` : "-",
      shiftsCompleted,
      amount: isCurrentMonth ? undefined : amount,
      noPayoutMsg:
        !isCurrentMonth && shiftsCompleted < 10
          ? "*10 shifts were not completed"
          : undefined,
    };
  });

  // ── Banner messages ──────────────────────────────────────────────
  const backendMessage = block.message ?? null;
const noPayoutMessage = backendMessage;
const successMessage  = null;

  return {
    monthKey: toMonthKey(monthStr),
    month: monthStr,
    isCurrentMonth,
    totalShifts,
    referralAmount,
    totalReferrals,
    eligibleWorkerCount,
    paymentStatus,  
    noPayoutMessage,
    successMessage,
    referrals,
  };
};

/**
 * Transform the full API data object into:
 *   { payoutData, totalPayout, totalEligible }
 */
const transformApiResponse = (data) => {
  if (!data) return { payoutData: [], totalPayout: 0, totalEligible: 0, totalReferrals: 0 };


  const currentMonthBlock = transformMonthBlock(data.currentMonth, true);
  const historyBlocks     = (data.history ?? []).map((h) => transformMonthBlock(h, false));

  // Deduplicate: if history somehow contains the current month, skip it
  const currentMonthKey = currentMonthBlock?.monthKey;
  const filteredHistory = historyBlocks.filter((h) => h && h.monthKey !== currentMonthKey);

  const payoutData = [
    ...(currentMonthBlock ? [currentMonthBlock] : []),
    ...filteredHistory,
  ];

  // Use top-level totals when available, fall back to derived values
  const totalPayout    = data.totalPayout           ?? 0;
  const totalEligible = data.totalReferralReceived ?? 0;
  const totalReferrals = data.totalReferrals         ?? 0;

  return { payoutData, totalPayout, totalEligible, totalReferrals };
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal Component
// ─────────────────────────────────────────────────────────────────────────────
const ReferralPayoutModal = ({ isOpen, onClose, workerId }) => {  // ← ADDED workerId prop
  const { user, fetchMonthlyPayout } = useSuperAdmin();

  const [payoutData,      setPayoutData]      = useState([]);
  const [totalPayout,     setTotalPayout]     = useState(0);
  const [totalEligible,   setTotalEligible]   = useState(0);
  const [expandedMonths,  setExpandedMonths]  = useState({});
  const [apiLoading,      setApiLoading]      = useState(true);
  const [apiError,        setApiError]        = useState(null);
  const [totalReferrals, setTotalReferrals] = useState(0);

  // ── Load data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!USE_REAL_API || !isOpen) return;

    const load = async () => {
      if (!workerId) {  // ← CHANGED: use workerId instead of user?.id
        setApiLoading(false);
        return;
      }
      setApiLoading(true);
      setApiError(null);

      try {
        const res = await fetchMonthlyPayout(workerId);  // ← CHANGED: pass workerId

        if (res?.success && res?.data) {
          const { payoutData: pd, totalPayout: tp, totalEligible: te, totalReferrals: tr } =
  transformApiResponse(res.data);
  setTotalReferrals(tr);

          setPayoutData(pd);
          setTotalPayout(tp);
          setTotalEligible(te);

          // Auto-expand the first (current) month
          if (pd.length > 0) {
            setExpandedMonths({ [pd[0].monthKey]: true });
          }
        } else {
          setPayoutData([]);
        }
      } catch (err) {
        console.error("Failed to load payout data:", err);
        setApiError("Failed to load payout data. Please try again.");
      } finally {
        setApiLoading(false);
      }
    };

    load();
  }, [workerId, isOpen]);  // ← CHANGED: depend on workerId instead of user?.id

  // ── Toggle ───────────────────────────────────────────────────────
  const toggleMonth = useCallback((monthKey) => {
    setExpandedMonths((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
  }, []);

  // ── Handle escape key ───────────────────────────────────────────
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // ── Handle body scroll lock ────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // ── Render Modal ─────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        padding: "16px",
        animation: "fadeIn 0.2s ease-out",
      }}
      onClick={onClose}
    >
      {/* Modal Container */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "520px",
          maxHeight: "90vh",
          background: "#f0f0f5",
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          animation: "slideUp 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 10,
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            border: "none",
            background: "rgba(0, 0, 0, 0.06)",
            fontSize: "20px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#333",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0, 0, 0, 0.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0, 0, 0, 0.06)")}
        >
          ✕
        </button>

        {/* Scrollable Content */}
        <div
          style={{
            overflowY: "auto",
            maxHeight: "90vh",
            paddingTop: "20px",
            paddingBottom: "20px",
          }}
        >
          <div style={{ maxWidth: 480, margin: "0 auto" }}>

            {/* Header */}
            <div className="relative flex items-center gap-4 justify-center mb-6" style={{ padding: "0 16px" }}>
              <h2 className="text-xl font-bold text-center">Referral Payout</h2>
            </div>

            {/* Summary Banner */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                padding: "4px 20px 20px",
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 2 }}>
                  Total Payout
                </div>
                <div
                  style={{
                    fontSize: 25,
                    fontWeight: 800,
                    color: "#08912A",
                    lineHeight: 1.1,
                    marginBottom: 10,
                  }}
                >
                  {apiLoading ? "-" : `₹${totalPayout}.00`}
                </div>
                <div style={{ fontSize: 11.5, color: "#555" }}>
                  Total Eligible Referrals :{" "}
                  <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 16 }}>
                    {apiLoading ? "-" : totalEligible}
                  </span>
                  <span style={{ color: "#000", fontWeight: 700, paddingLeft: 4, fontSize: 12 }}>
                    / {apiLoading ? "-" : totalReferrals}  
                  </span>
                </div>
              </div>
              <img
                src={referral}
                alt="Referral Illustration"
                style={{ height: 100, margin: "0px 50px" }}
              />
            </div>

            {/* Content */}
            {apiLoading ? (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#888", fontSize: 14 }}>
                Loading payout data…
              </div>
            ) : apiError ? (
              <div
                style={{
                  margin: "0 12px",
                  padding: "16px",
                  background: "#fff0f0",
                  borderRadius: 12,
                  color: "#c0392b",
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                {apiError}
              </div>
            ) : payoutData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#aaa", fontSize: 14 }}>
                No payout data available.
              </div>
            ) : (
              <div
                style={{
                  padding: "0 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  paddingBottom: 32,
                }}
              >
                {payoutData.map((monthData) => (
                  <ReferralPayoutCard
                    key={monthData.monthKey}
                    month={monthData.month}
                    isCurrentMonth={monthData.isCurrentMonth}
                    totalShifts={monthData.totalShifts}
                    referralAmount={monthData.referralAmount}
                    eligibleReferralsCount={monthData.eligibleWorkerCount}
                    totalReferrals={monthData.totalReferrals}
                    referrals={monthData.referrals}
                    noPayoutMessage={monthData.noPayoutMessage}
                    successMessage={monthData.successMessage}
                    isExpanded={!!expandedMonths[monthData.monthKey]}
                    onToggle={() => toggleMonth(monthData.monthKey)}
                    currentEligibleCount={totalReferrals}
                  />
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.98);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ReferralPayoutModal;